import type { CallSessionId, PublicKey } from '@shared/types';
import { webrtcManager } from '../services/webrtc';
import {
  answerNativeIncomingVideoCall,
  endNativeAudioRuntimeCall,
  startNativeOutgoingVideoCall
} from '../services/callRuntime';
import { isWebViewMode } from '../utils/webviewBridge';
import { requestWebViewMediaPermissions } from '../utils/webviewMediaPermissions';
import type { SigningKey } from '../crypto/ed25519Provider';

type MediaSetupParams = {
  withVideo: boolean;
};

type StartOutgoingParams = {
  callSessionId: CallSessionId;
  remoteIdentityId: string;
  remoteDisplayName?: string;
  remoteIdentityPublicKey?: PublicKey;
  localIdentityId?: string;
  localDeviceId?: string;
  withVideo: boolean;
  serverUrl?: string;
  signingKey?: SigningKey;
  deviceSigningKey?: SigningKey;
};

type AnswerIncomingParams = {
  callSessionId: CallSessionId;
  remoteIdentityId: string;
  remoteDisplayName?: string;
  remoteIdentityPublicKey?: PublicKey;
  localIdentityId?: string;
  localDeviceId?: string;
  withVideo?: boolean;
  serverUrl?: string;
  signingKey?: SigningKey;
  deviceSigningKey?: SigningKey;
  offer: RTCSessionDescriptionInit & { dtlsSignature?: string };
};

async function ensureWebViewCallPermissions(params: MediaSetupParams): Promise<void> {
  await requestWebViewMediaPermissions({ audio: true, video: params.withVideo });
}

export interface CallRuntimeAdapter {
  prepareLocalMedia(params: MediaSetupParams): Promise<void>;
  startOutgoing(params: StartOutgoingParams): Promise<void>;
  answerIncoming(params: AnswerIncomingParams): Promise<void>;
  end(callSessionId: CallSessionId): void;
}

class WebCallRuntimeAdapter implements CallRuntimeAdapter {
  async prepareLocalMedia(params: MediaSetupParams): Promise<void> {
    if (isWebViewMode) {
      await ensureWebViewCallPermissions(params);
    }
    await webrtcManager.getLocalMedia(params.withVideo, true);
  }

  async startOutgoing(params: StartOutgoingParams): Promise<void> {
    if (!params.signingKey) {
      throw new Error('Missing signing key');
    }
    await webrtcManager.initiateCall(
      params.remoteIdentityId,
      params.callSessionId,
      params.signingKey,
      {
        deviceId: params.localDeviceId,
        identityId: params.localIdentityId,
        identitySigningKey: params.signingKey,
        deviceSigningKey: params.deviceSigningKey,
        serverUrl: params.serverUrl
      }
    );
  }

  async answerIncoming(params: AnswerIncomingParams): Promise<void> {
    if (!params.signingKey) {
      throw new Error('Missing signing key');
    }
    await webrtcManager.answerCall(
      params.callSessionId,
      params.offer,
      params.signingKey,
      params.remoteIdentityId,
      {
        deviceId: params.localDeviceId,
        identityId: params.localIdentityId,
        identitySigningKey: params.signingKey,
        deviceSigningKey: params.deviceSigningKey,
        serverUrl: params.serverUrl
      }
    );
  }

  end(): void {
    webrtcManager.close();
  }
}

class NativeVideoCallRuntimeAdapter implements CallRuntimeAdapter {
  async prepareLocalMedia(): Promise<void> {
    // Native calls intentionally use the video-native runtime even when the
    // call starts as audio-only, so mid-call video enablement can reuse the
    // same native session. Preflight both permissions before the service starts.
    await requestWebViewMediaPermissions({ audio: true, video: true });
  }

  async startOutgoing(params: StartOutgoingParams): Promise<void> {
    await startNativeOutgoingVideoCall({
      callSessionId: params.callSessionId,
      remoteIdentityId: params.remoteIdentityId,
      remoteDisplayName: params.remoteDisplayName,
      remoteIdentityPublicKey: params.remoteIdentityPublicKey,
      localVideoEnabled: params.withVideo,
      serverUrl: params.serverUrl,
      localDeviceId: params.localDeviceId,
      localIdentityId: params.localIdentityId,
      deviceSigningKey: params.deviceSigningKey
    });
  }

  async answerIncoming(params: AnswerIncomingParams): Promise<void> {
    await answerNativeIncomingVideoCall({
      callSessionId: params.callSessionId,
      remoteIdentityId: params.remoteIdentityId,
      remoteDisplayName: params.remoteDisplayName,
      remoteIdentityPublicKey: params.remoteIdentityPublicKey,
      localVideoEnabled: params.withVideo,
      serverUrl: params.serverUrl,
      localDeviceId: params.localDeviceId,
      localIdentityId: params.localIdentityId,
      deviceSigningKey: params.deviceSigningKey,
      offer: params.offer
    });
  }

  end(callSessionId: CallSessionId): void {
    endNativeAudioRuntimeCall(callSessionId);
  }
}

const webCallRuntimeAdapter = new WebCallRuntimeAdapter();
const nativeVideoCallRuntimeAdapter = new NativeVideoCallRuntimeAdapter();

export function getCallRuntimeAdapter(): CallRuntimeAdapter {
  return isWebViewMode ? nativeVideoCallRuntimeAdapter : webCallRuntimeAdapter;
}
